/**
 * DataGridCard Component
 *
 * Complete DataGrid experience combining:
 * - Toolbar (composed using DataGridToolbar) - OUTSIDE card
 * - DataGridTable (grid, pagination) - INSIDE card
 *
 * Usage:
 * ```tsx
 * <DataGridCard
 *   items={users}
 *   columns={columns}
 *   getRowId={(item) => item.id}
 *   toolbar={
 *     <DataGridToolbar>
 *       <DataGridToolbar.Left>
 *         <ToolbarButton icon={<RefreshIcon />} onClick={refetch}>Refresh</ToolbarButton>
 *       </DataGridToolbar.Left>
 *       <DataGridToolbar.Right>
 *         <ToolbarSearch value={search} onChange={setSearch} />
 *         <ToolbarButton appearance="primary" icon={<AddIcon />}>Add</ToolbarButton>
 *       </DataGridToolbar.Right>
 *     </DataGridToolbar>
 *   }
 *   pagination={{ showing: 10, total: 100 }}
 * />
 * ```
 */

import { ReactNode } from 'react';
import { TableColumnDefinition, TableColumnSizingOptions, SortDirection } from '@fluentui/react-components';
import { DataGridTable } from './DataGridTable';

export interface DataGridCardProps<T> {
  /** Items to display in the grid */
  items: T[];
  /** Column definitions */
  columns: TableColumnDefinition<T>[];
  /** Function to get unique row ID */
  getRowId: (item: T) => string | number;
  /** Column sizing options */
  columnSizingOptions?: TableColumnSizingOptions;
  /** Default sort state */
  defaultSortState?: {
    sortColumn: string;
    sortDirection: SortDirection;
  };
  /** Composed toolbar - use DataGridToolbar with ToolbarButton/ToolbarSearch */
  toolbar?: ReactNode;
  /** Pagination configuration */
  pagination: {
    showing: number;
    total: number;
    page?: number;
    totalPages?: number;
    onPageChange?: (page: number) => void;
  };
  /** Enable selection mode */
  selectable?: boolean;
  /** Currently selected IDs */
  selectedIds?: Set<string | number>;
  /** Callback when selection changes */
  onSelectionChange?: (ids: Set<string | number>) => void;
  /** Loading state */
  loading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Test ID for the component */
  testId?: string;
}

export function DataGridCard<T>({
  items,
  columns,
  getRowId,
  columnSizingOptions,
  defaultSortState,
  toolbar,
  pagination,
  selectable = false,
  selectedIds,
  onSelectionChange,
  loading = false,
  emptyMessage,
  testId,
}: DataGridCardProps<T>) {
  return (
    <div data-testid={testId}>
      {toolbar}
      <DataGridTable
        items={items}
        columns={columns}
        getRowId={getRowId}
        columnSizingOptions={columnSizingOptions}
        defaultSortState={defaultSortState}
        pagination={pagination}
        selectable={selectable}
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
        loading={loading}
        emptyMessage={emptyMessage}
      />
    </div>
  );
}
